import { useEffect, useState } from 'preact/hooks'
import { getBlob } from '~/storage/blobStore'
import { useDatabase } from '~/storage/DatabaseContext'
import type { BlobId } from '~/types/ids'

/**
 * Resolve a stored blob to an object URL, and revoke it on the way out.
 *
 * **The revoke is the whole point.** An object URL pins its blob in memory
 * until it is revoked, so a photo grid that creates one per thumbnail and never
 * releases them holds the entire library open — which is the iOS memory failure
 * Checkpoint 3 exists to avoid. `URL.revokeObjectURL` runs on unmount and
 * whenever the id changes, so scrolling a long timeline does not accumulate.
 *
 * Returns `undefined` while loading and when the blob is missing. A missing
 * blob is drawn as a broken-image placeholder by the caller rather than an
 * error: it means the record and its bytes disagree, which is worth showing
 * plainly rather than crashing a screen full of good photos.
 */
export function useBlobUrl(blobId: BlobId | undefined): string | undefined {
  const db = useDatabase()
  const [url, setUrl] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (blobId === undefined) {
      setUrl(undefined)
      return
    }

    let cancelled = false
    let created: string | undefined

    void (async () => {
      const stored = await getBlob(db, blobId)
      if (cancelled) return
      if (!stored.ok || stored.value === undefined) {
        setUrl(undefined)
        return
      }
      created = URL.createObjectURL(stored.value.blob)
      /* The effect may have been torn down while the read was in flight. Revoke
         immediately rather than handing back a URL nothing will ever release. */
      if (cancelled) {
        URL.revokeObjectURL(created)
        return
      }
      setUrl(created)
    })()

    return () => {
      cancelled = true
      if (created !== undefined) URL.revokeObjectURL(created)
    }
  }, [db, blobId])

  return url
}
